/**
 * Reaction Service
 *
 * Manages emoji reactions on messages.
 * Reactions are tracked per user per message per emoji.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import { batchGetProfilePhotoSignedUrls } from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import type { ReactionWithUser } from '../../types';

export class ReactionService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Add a reaction to a message
   * Silently succeeds if reaction already exists
   */
  async addReaction(messageId: string, emoji: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    if (!emoji || emoji.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Emoji is required'),
      );
    }

    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    const { error } = await this.client.from('message_reactions').upsert(
      {
        message_id: messageId,
        user_id: user.id,
        emoji: emoji.trim(),
      },
      {
        onConflict: 'message_id,user_id,emoji',
        ignoreDuplicates: true,
      },
    );

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Remove a reaction from a message
   */
  async removeReaction(
    messageId: string,
    emoji: string,
  ): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    if (!emoji || emoji.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Emoji is required'),
      );
    }

    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    const { error } = await this.client
      .from('message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', user.id)
      .eq('emoji', emoji.trim());

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Toggle a reaction (add if not exists, remove if exists)
   */
  async toggleReaction(
    messageId: string,
    emoji: string,
  ): Promise<Result<{ added: boolean }>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    if (!emoji || emoji.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Emoji is required'),
      );
    }

    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    // Check if reaction exists
    const { data: existing } = await this.client
      .from('message_reactions')
      .select('id')
      .eq('message_id', messageId)
      .eq('user_id', user.id)
      .eq('emoji', emoji.trim())
      .maybeSingle();

    if (existing) {
      // Remove the reaction
      const removeResult = await this.removeReaction(messageId, emoji);
      if (removeResult.isFailure) {
        return Result.fail(removeResult.getError());
      }
      return Result.ok({ added: false });
    } else {
      // Add the reaction
      const addResult = await this.addReaction(messageId, emoji);
      if (addResult.isFailure) {
        return Result.fail(addResult.getError());
      }
      return Result.ok({ added: true });
    }
  }

  /**
   * Get all reactions for a message with user details
   */
  async getReactions(messageId: string): Promise<Result<ReactionWithUser[]>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('message_reactions')
      .select('*')
      .eq('message_id', messageId)
      .order('created_at', { ascending: true });

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const reactionRows = data || [];

    // Fetch user profiles in batch (no FK from message_reactions to profiles)
    const userIds = [...new Set(reactionRows.map((r) => r.user_id))];
    const { data: profiles } =
      userIds.length > 0
        ? await this.client
            .from('profiles')
            .select('id, first_name, last_name, profile_photo_url')
            .in('id', userIds)
        : { data: [] };

    const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

    // Collect profile photo paths
    const profilePhotoPaths = (profiles || [])
      .map((p) => p.profile_photo_url)
      .filter((p): p is string => !!p);

    // Generate signed URLs in batch
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      profilePhotoPaths,
    );

    const reactions: ReactionWithUser[] = reactionRows.map((r) => {
      const profile = profileMap.get(r.user_id);
      return {
        ...r,
        user: profile
          ? {
              ...profile,
              profile_photo_url: profile.profile_photo_url
                ? (signedUrlMap.get(profile.profile_photo_url) ?? null)
                : null,
            }
          : {
              id: r.user_id,
              first_name: 'Unknown',
              last_name: null,
              profile_photo_url: null,
            },
      };
    });

    return Result.ok(reactions);
  }

  /**
   * Get users who reacted with a specific emoji on a message
   */
  async getReactionUsers(
    messageId: string,
    emoji: string,
  ): Promise<Result<ReactionWithUser[]>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('message_reactions')
      .select('*')
      .eq('message_id', messageId)
      .eq('emoji', emoji.trim())
      .order('created_at', { ascending: true });

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const reactionRows = data || [];

    // Fetch user profiles in batch (no FK from message_reactions to profiles)
    const userIds = [...new Set(reactionRows.map((r) => r.user_id))];
    const { data: profiles } =
      userIds.length > 0
        ? await this.client
            .from('profiles')
            .select('id, first_name, last_name, profile_photo_url')
            .in('id', userIds)
        : { data: [] };

    const profileMap = new Map((profiles || []).map((p) => [p.id, p]));

    // Collect profile photo paths
    const profilePhotoPaths = (profiles || [])
      .map((p) => p.profile_photo_url)
      .filter((p): p is string => !!p);

    // Generate signed URLs in batch
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      profilePhotoPaths,
    );

    const reactions: ReactionWithUser[] = reactionRows.map((r) => {
      const profile = profileMap.get(r.user_id);
      return {
        ...r,
        user: profile
          ? {
              ...profile,
              profile_photo_url: profile.profile_photo_url
                ? (signedUrlMap.get(profile.profile_photo_url) ?? null)
                : null,
            }
          : {
              id: r.user_id,
              first_name: 'Unknown',
              last_name: null,
              profile_photo_url: null,
            },
      };
    });

    return Result.ok(reactions);
  }
}
